import { Controller, useFormContext } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import styled, { css } from 'styled-components';
import { useEffect, useState } from 'react';
import { LuTarget, LuSplit, LuRoute, LuScissors } from 'react-icons/lu';

import { getUserTags } from '@utils/api';
import Input from '@components/form/Input';
import ProjectTagsSelect from '@components/projectComponents/projectTags/ProjectTagsSelect';
import { modelForSubject } from '@config/videoDetectionModelConfig';
import {
	flow,
	FlowPanel,
	Label,
	PanelTitle,
	Small,
} from '@routes/userRoutes/projects/videoProject/flow/primitives';
import { clampTrim, formatTrimTime, parseTrimTime } from './trimTime';

/** Wizard parts: the project details card (left column) and the counting choices (right column, step 2). */

export const ProjectDetailsCard = ({ disabled, onChangeType }) => {
	const { control } = useFormContext();
	const { data: userTags } = useQuery({
		queryKey: ['user_tags'],
		queryFn: getUserTags,
		refetchOnWindowFocus: false,
	});
	return (
		<FlowPanel aria-label="Project details">
			<Label>Project</Label>
			<Controller
				name="projectName"
				control={control}
				rules={{ required: 'Project name is required' }}
				render={({ field }) => (
					<Input
						{...field}
						value={field.value || ''}
						label="Name *"
						type="text"
						disabled={disabled}
						required
					/>
				)}
			/>
			<Controller
				name="projectDescription"
				control={control}
				render={({ field }) => (
					<Input
						{...field}
						value={field.value || ''}
						label="Description"
						type="text"
						disabled={disabled}
					/>
				)}
			/>
			{userTags && (
				<Controller
					name="projectTags"
					control={control}
					render={({ field }) => (
						<ProjectTagsSelect label="Tags" userTags={userTags} field={field} />
					)}
				/>
			)}
			{onChangeType && !disabled && (
				<button
					type="button"
					onClick={onChangeType}
					style={{
						background: 'none',
						border: 0,
						padding: 0,
						color: flow.text3,
						fontSize: '0.75rem',
						textAlign: 'left',
					}}>
					Not a video? <u>Choose another project type</u>
				</button>
			)}
		</FlowPanel>
	);
};

/** The three ways to count, mapped to the backend's scenario + method (src/app/schemas/video.py). */
export const COUNT_MODES = [
	{
		id: 'hover',
		title: 'Hovering · total count',
		hint: 'The drone holds still over the herd',
		scenario: 'stationary_drone',
		method: 'unique_id',
		Icon: LuTarget,
	},
	{
		id: 'line',
		title: 'Line crossing',
		hint: 'Animals walk past a line you draw on the video',
		scenario: 'fence_exit',
		method: 'line_based',
		Icon: LuSplit,
	},
	{
		id: 'moving',
		title: 'Flying along the herd',
		hint: 'The drone moves; each animal is counted once',
		scenario: 'moving_drone',
		method: 'unique_id',
		Icon: LuRoute,
	},
];

export const modeOf = c =>
	COUNT_MODES.find(m => m.scenario === c?.countingScenario && m.method === c?.countingMethod) ||
	null;

const Cards = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

const Card = styled.button`
	display: grid;
	grid-template-columns: 34px 1fr;
	gap: 0.75rem;
	align-items: center;
	padding: 0.7rem 0.75rem;
	border-radius: 12px;
	text-align: left;
	color: ${flow.text};
	border: 1px solid #343834;
	background: rgba(255, 255, 255, 0.015);
	cursor: pointer;
	b {
		display: block;
		font-size: 0.85rem;
		font-weight: 600;
	}
	span.hint {
		display: block;
		margin-top: 2px;
		font-size: 0.72rem;
		line-height: 1.35;
		color: ${flow.text3};
	}
	i {
		width: 34px;
		height: 34px;
		border-radius: 9px;
		background: #2a2d2a;
		display: grid;
		place-items: center;
		color: ${flow.text2};
	}
	&:hover:not(:disabled) {
		border-color: #4a504a;
	}
	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}
	&:focus-visible {
		outline: 2px solid ${({ theme }) => theme.colors.lightGreen};
		outline-offset: 2px;
	}
	${({ $on, theme }) =>
		$on &&
		css`
			border-color: ${theme.colors.green};
			background: ${flow.greenFill};
			i {
				background: ${theme.colors.darkGreen};
				color: #fff;
			}
		`}
`;

const SuggestedTag = styled.span`
	margin-left: ${({ theme }) => theme.token.marginXS}px;
	padding: 0 ${({ theme }) => theme.token.paddingXS}px;
	border-radius: ${({ theme }) => theme.token.borderRadiusSM}px;
	font-size: ${({ theme }) => theme.token.fontSizeXXS}px;
	font-weight: ${({ theme }) => theme.token.fontWeightStrong};
	color: ${({ theme }) => theme.token.colorPrimary};
	border: ${({ theme }) =>
		`${theme.token.lineWidth}px ${theme.token.lineType} ${theme.token.colorPrimary}`};
	vertical-align: middle;
`;

export const CountModeCards = ({ value, onChange, lineDisabled, suggested }) => (
	<Cards role="radiogroup" aria-label="How to count">
		{COUNT_MODES.map(({ id, title, hint, Icon, ...m }) => {
			const disabled = id === 'line' && lineDisabled;
			return (
				<Card
					key={id}
					type="button"
					role="radio"
					aria-checked={value === id}
					$on={value === id}
					disabled={disabled}
					onClick={() => onChange({ id, ...m })}>
					<i aria-hidden="true">
						<Icon size={17} />
					</i>
					<span>
						<b>
							{title}
							{suggested === id && <SuggestedTag>AI suggestion</SuggestedTag>}
						</b>
						<span className="hint">
							{disabled ? 'Needs a video the browser can play' : hint}
						</span>
					</span>
				</Card>
			);
		})}
	</Cards>
);

const SubjectInput = styled.input`
	width: 100%;
	padding: 0.75rem 0.9rem;
	border-radius: 10px;
	border: 1px solid #343834;
	background: #151715;
	color: ${flow.text};
	font-size: 1rem;
`;

/** What to find, typed or picked from the AI suggestion: the first question of the counting step. The model follows
 * the word (no model names shown); a broad request ("find all vehicles") is split into kinds when the job starts. */
export const SubjectField = ({ value, onChange }) => {
	const model = modelForSubject(value);
	return (
		<label style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', margin: 0 }}>
			<PanelTitle as="span">What do you want to find?</PanelTitle>
			<SubjectInput
				value={value || ''}
				onChange={e => onChange(e.target.value)}
				placeholder="reindeer, sheep, cars… or “find all vehicles”"
				aria-describedby="subject-model"
			/>
			<Small id="subject-model">
				<b>{model.label}</b> · {model.description}
			</Small>
		</label>
	);
};

const TrimGrid = styled.div`
	display: grid;
	grid-template-columns: auto 1fr auto;
	gap: 0.4rem 0.5rem;
	align-items: center;
	font-size: 0.78rem;
	color: ${flow.text2};
`;

const TimeInput = styled.input`
	width: 100%;
	padding: 0.45rem 0.6rem;
	border-radius: 9px;
	border: 1px solid ${({ $bad }) => ($bad ? '#e25a4a' : '#343834')};
	background: #151715;
	color: ${flow.text};
	font-size: 0.82rem;
	font-variant-numeric: tabular-nums;
`;

const SmallButton = styled.button`
	padding: 0.4rem 0.6rem;
	border-radius: 9px;
	border: 1px solid #3a3e3a;
	background: transparent;
	color: ${flow.text2};
	font-size: 0.72rem;
	white-space: nowrap;
	cursor: pointer;
	&:hover:not(:disabled) {
		border-color: ${({ theme }) => theme.colors.green};
		color: ${flow.text};
	}
	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}
	&:focus-visible {
		outline: 2px solid ${({ theme }) => theme.colors.lightGreen};
		outline-offset: 2px;
	}
`;

const TimeField = ({ label, seconds, onCommit }) => {
	const [text, setText] = useState(formatTrimTime(seconds));
	useEffect(() => setText(formatTrimTime(seconds)), [seconds]);
	const bad = parseTrimTime(text) === null;
	const commit = () => {
		const v = parseTrimTime(text);
		if (v === null) setText(formatTrimTime(seconds));
		else onCommit(v);
	};
	return (
		<TimeInput
			aria-label={`${label} time (minutes:seconds)`}
			inputMode="decimal"
			value={text}
			$bad={bad}
			onChange={e => setText(e.target.value)}
			onBlur={commit}
			onKeyDown={e => e.key === 'Enter' && commit()}
		/>
	);
};

/**
 * Trim before the upload: only this part of the video is analysed (the backend cuts it out before detection). The
 * film strip under the video has the same range as drag handles; this is the precise, keyboard-friendly version.
 * `getNow` returns the player position, for "Set to playhead".
 */
export const TrimField = ({ range, duration, getNow, onChange }) => {
	if (!range || !duration) return null;
	const set = (which, v) => onChange(clampTrim(range, which, v, duration));
	const whole = range.start_seconds <= 0 && range.end_seconds >= duration - 0.05;
	const length = range.end_seconds - range.start_seconds;
	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
			<span
				style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<Label>
					<LuScissors aria-hidden style={{ marginRight: 6, verticalAlign: '-2px' }} />
					Part to analyse
				</Label>
				<span
					style={{
						fontSize: '0.75rem',
						color: flow.text2,
						fontVariantNumeric: 'tabular-nums',
					}}>
					{whole ? 'Whole video' : `${Math.round(length)} s of ${Math.round(duration)} s`}
				</span>
			</span>
			<TrimGrid>
				<span>Start</span>
				<TimeField
					label="Start"
					seconds={range.start_seconds}
					onCommit={v => set('start', v)}
				/>
				<SmallButton type="button" onClick={() => set('start', getNow())}>
					Set to playhead
				</SmallButton>
				<span>End</span>
				<TimeField label="End" seconds={range.end_seconds} onCommit={v => set('end', v)} />
				<SmallButton type="button" onClick={() => set('end', getNow())}>
					Set to playhead
				</SmallButton>
			</TrimGrid>
			<span
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					gap: 8,
				}}>
				<span style={{ fontSize: '0.7rem', color: flow.text3 }}>
					Or drag the white handles on the strip under the video.
				</span>
				<SmallButton
					type="button"
					disabled={whole}
					onClick={() =>
						onChange({
							start_seconds: 0,
							end_seconds: Math.round(duration * 100) / 100,
						})
					}>
					Whole video
				</SmallButton>
			</span>
		</div>
	);
};
